// ============================================
// Lab Calibration Dashboard
//
// Three sections: Import | Cases | Launch Gate
// NO framer-motion — pure CSS transitions only.
// ============================================

import { useState, useEffect, useRef } from 'react';
import { Upload, FileText, BarChart3, Settings, RefreshCw, Lightbulb, CheckCircle, XCircle, TrendingUp } from 'lucide-react';
import type { CalibrationCase, CaseDemographics, LaunchGateStatus, LaunchGateThresholds, DivergenceSummary, CalibrationCaseStatus } from '../../../types/labUpload';
import { DEFAULT_LAUNCH_GATE_THRESHOLDS } from '../../../types/labUpload';
import { getCalibrationCases, getCalibrationStats } from '../../../services/firestore/calibrationCaseService';
import { getConvergenceStats } from '../../../services/calibration/convergenceService';
import { runCalibrationPipeline } from '../../../services/calibration/calibrationPipeline';
import { computeDemographicDistribution } from '../../../services/calibration/demographicCheck';
import { getDivergenceSummaries, approveDivergenceSummary, rejectDivergenceSummary } from '../../../services/calibration/divergenceSummary';
import { computeLaunchGate as computeLaunchGateService, updateThresholds, getThresholds } from '../../../services/calibration/launchGateService';
import { CalibrationCaseCard } from './CalibrationCaseCard';
import { DrCReviewQueue } from './DrCReviewQueue';
import { FormularyMappingEditor } from './FormularyMappingEditor';

type SubView = 'import' | 'cases' | 'review' | 'convergence' | 'launch-gate' | 'mappings' | 'improvements';

const BRAND_ID = 'hemologix'; // Default brand

export function LabCalibration() {
  const [subView, setSubView] = useState<SubView>('import');
  const [cases, setCases] = useState<CalibrationCase[]>([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<{ total: number; byStatus: Record<string, number>; avgAlignment: number; reviewedCount: number } | null>(null);

  // Import state
  const [labFile, setLabFile] = useState<File | null>(null);
  const [soapFile, setSoapFile] = useState<File | null>(null);
  const [age, setAge] = useState('');
  const [sex, setSex] = useState<'Male' | 'Female' | ''>('');
  const [importing, setImporting] = useState(false);
  const [importStatus, setImportStatus] = useState('');
  const labInputRef = useRef<HTMLInputElement>(null);
  const soapInputRef = useRef<HTMLInputElement>(null);

  // Case filter state
  const [caseStatusFilter, setCaseStatusFilter] = useState<CalibrationCaseStatus | 'all'>('all');

  // Divergence summary state
  const [summaries, setSummaries] = useState<DivergenceSummary[]>([]);
  const [summaryPrompts, setSummaryPrompts] = useState<Record<string, string>>({});
  const [summaryRejectReasons, setSummaryRejectReasons] = useState<Record<string, string>>({});
  const [summaryActionLoading, setSummaryActionLoading] = useState<string | null>(null);

  // Launch gate threshold editor state
  const [thresholdEdits, setThresholdEdits] = useState<LaunchGateThresholds>(DEFAULT_LAUNCH_GATE_THRESHOLDS);
  const [thresholdsLoading, setThresholdsLoading] = useState(false);
  const [thresholdsSaved, setThresholdsSaved] = useState(false);

  // Convergence state
  const [convergenceStats, setConvergenceStats] = useState<{
    total: number;
    modelCorrect: number;
    drCorrect: number;
    bothCorrect: number;
    avgAlignment: number;
    modelCorrectRate: number;
    trendByBatch: Array<{ batchStart: number; batchEnd: number; avgAlignment: number }>;
    biasWarning: string | null;
  } | null>(null);
  const [convergenceLoading] = useState(false);

  // Recompute gate state
  const [recomputeLoading, setRecomputeLoading] = useState(false);
  const [recomputedGate, setRecomputedGate] = useState<LaunchGateStatus | null>(null);

  async function loadData() {
    setLoading(true);
    try {
      const [allCases, caseStats, allSummaries, currentThresholds, convStats] = await Promise.all([
        getCalibrationCases(BRAND_ID),
        getCalibrationStats(BRAND_ID),
        getDivergenceSummaries(),
        getThresholds(),
        getConvergenceStats(),
      ]);
      setCases(allCases);
      setStats(caseStats);
      setSummaries(allSummaries);
      setThresholdEdits(currentThresholds);
      setConvergenceStats(convStats);
    } catch (err) {
      console.error('[LabCalibration] Load failed:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadData(); }, []);

  async function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async function handleImport() {
    if (!labFile || !soapFile) return;
    setImporting(true);
    setImportStatus('Starting pipeline...');

    try {
      const labBase64 = await fileToBase64(labFile);
      const soapBase64 = await fileToBase64(soapFile);

      const demographics: CaseDemographics | undefined =
        (age || sex) ? { age: age ? parseInt(age) : undefined, sex: sex || undefined } : undefined;

      setImportStatus('Running extraction + blind analysis + comparison...');

      await runCalibrationPipeline(
        labBase64,
        labFile.type || 'application/pdf',
        labFile.name,
        soapBase64,
        soapFile.type || 'application/pdf',
        soapFile.name,
        BRAND_ID,
        'dr-calapai',
        demographics,
      );

      setImportStatus('Complete!');
      setLabFile(null);
      setSoapFile(null);
      setAge('');
      setSex('');
      await loadData();
    } catch (err) {
      setImportStatus(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setImporting(false);
    }
  }

  // Launch gate computation
  const gateStatus: LaunchGateStatus | null = stats ? computeLaunchGate(cases, stats) : null;

  // Filtered cases for the cases view
  const filteredCases = caseStatusFilter === 'all'
    ? cases
    : cases.filter((c) => c.status === caseStatusFilter);

  // Handlers for divergence summaries
  async function handleApproveSummary(summaryId: string) {
    const prompt = summaryPrompts[summaryId];
    if (!prompt?.trim()) return;
    setSummaryActionLoading(summaryId);
    try {
      await approveDivergenceSummary(summaryId, prompt, 'Approved via calibration dashboard');
      await loadData();
    } catch (err) {
      console.error('[LabCalibration] Approve failed:', err);
    } finally {
      setSummaryActionLoading(null);
    }
  }

  async function handleRejectSummary(summaryId: string) {
    const reason = summaryRejectReasons[summaryId];
    if (!reason?.trim()) return;
    setSummaryActionLoading(summaryId);
    try {
      await rejectDivergenceSummary(summaryId, reason);
      await loadData();
    } catch (err) {
      console.error('[LabCalibration] Reject failed:', err);
    } finally {
      setSummaryActionLoading(null);
    }
  }

  // Handler for threshold save
  async function handleSaveThresholds() {
    setThresholdsLoading(true);
    setThresholdsSaved(false);
    try {
      await updateThresholds(thresholdEdits);
      setThresholdsSaved(true);
      setTimeout(() => setThresholdsSaved(false), 3000);
    } catch (err) {
      console.error('[LabCalibration] Save thresholds failed:', err);
    } finally {
      setThresholdsLoading(false);
    }
  }

  // Handler for recompute gate
  async function handleRecomputeGate() {
    setRecomputeLoading(true);
    setRecomputedGate(null);
    try {
      const result = await computeLaunchGateService(BRAND_ID);
      setRecomputedGate(result);
      await loadData();
    } catch (err) {
      console.error('[LabCalibration] Recompute gate failed:', err);
    } finally {
      setRecomputeLoading(false);
    }
  }

  const tabs: { id: SubView; label: string; icon: typeof Upload }[] = [
    { id: 'import', label: 'Import', icon: Upload },
    { id: 'cases', label: 'Cases', icon: FileText },
    { id: 'review', label: 'Dr. C Review', icon: FileText },
    { id: 'convergence', label: 'Convergence', icon: TrendingUp },
    { id: 'launch-gate', label: 'Launch Gate', icon: BarChart3 },
    { id: 'mappings', label: 'Mappings', icon: Settings },
    { id: 'improvements', label: 'Improvements', icon: Lightbulb },
  ];

  return (
    <div>
      {/* Stats bar */}
      {stats && (
        <div className="flex gap-4 mb-4">
          <StatBox label="Total Cases" value={stats.total} />
          <StatBox label="Reviewed" value={stats.reviewedCount} />
          <StatBox label="Avg Alignment" value={`${stats.avgAlignment}%`} />
          <StatBox
            label="Launch Gate"
            value={gateStatus?.passing ? 'PASS' : 'NOT YET'}
            color={gateStatus?.passing ? 'text-green-700' : 'text-amber-600'}
          />
        </div>
      )}

      {/* Sub-navigation */}
      <div className="flex gap-1 mb-4 border-b border-zinc-200">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setSubView(id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              subView === id
                ? 'border-rose-500 text-rose-600'
                : 'border-transparent text-zinc-500 hover:text-zinc-700'
            }`}
          >
            <Icon size={14} />
            {label}
          </button>
        ))}
      </div>

      {/* Import View */}
      {subView === 'import' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Lab PDF Drop Zone */}
            <div
              onClick={() => labInputRef.current?.click()}
              className={`border-2 border-dashed rounded p-6 text-center cursor-pointer transition-colors ${
                labFile ? 'border-green-300 bg-green-50' : 'border-zinc-300 hover:border-rose-300'
              }`}
            >
              <Upload size={24} className="mx-auto mb-2 text-zinc-500" />
              <p className="text-sm font-medium text-zinc-700">
                {labFile ? labFile.name : 'Lab Report PDF'}
              </p>
              <p className="text-[13px] text-zinc-500 mt-1">Click to select</p>
              <input
                ref={labInputRef}
                type="file"
                accept=".pdf,image/*"
                className="hidden"
                onChange={(e) => setLabFile(e.target.files?.[0] ?? null)}
              />
            </div>

            {/* SOAP PDF Drop Zone */}
            <div
              onClick={() => soapInputRef.current?.click()}
              className={`border-2 border-dashed rounded p-6 text-center cursor-pointer transition-colors ${
                soapFile ? 'border-green-300 bg-green-50' : 'border-zinc-300 hover:border-rose-300'
              }`}
            >
              <FileText size={24} className="mx-auto mb-2 text-zinc-500" />
              <p className="text-sm font-medium text-zinc-700">
                {soapFile ? soapFile.name : 'SOAP Note PDF'}
              </p>
              <p className="text-[13px] text-zinc-500 mt-1">Click to select</p>
              <input
                ref={soapInputRef}
                type="file"
                accept=".pdf,image/*"
                className="hidden"
                onChange={(e) => setSoapFile(e.target.files?.[0] ?? null)}
              />
            </div>
          </div>

          {/* Demographics */}
          <div className="flex gap-4">
            <div className="w-32">
              <label className="text-[13px] text-zinc-500 block mb-1">Age</label>
              <input
                value={age}
                onChange={(e) => setAge(e.target.value)}
                type="number"
                placeholder="e.g. 52"
                className="w-full border border-zinc-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-rose-500"
              />
            </div>
            <div className="w-40">
              <label className="text-[13px] text-zinc-500 block mb-1">Sex</label>
              <select
                value={sex}
                onChange={(e) => setSex(e.target.value as 'Male' | 'Female' | '')}
                className="w-full border border-zinc-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-rose-500"
              >
                <option value="">Select...</option>
                <option value="Female">Female</option>
                <option value="Male">Male</option>
              </select>
            </div>
          </div>

          {/* Import Button */}
          <button
            onClick={handleImport}
            disabled={!labFile || !soapFile || importing}
            className="px-6 py-3 bg-rose-600 text-white rounded text-sm font-semibold hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {importing ? <RefreshCw size={16} className="animate-spin" /> : <Upload size={16} />}
            {importing ? 'Processing...' : 'Import Case'}
          </button>

          {importStatus && (
            <p className={`text-sm ${importStatus.startsWith('Error') ? 'text-red-600' : 'text-zinc-600'}`}>
              {importStatus}
            </p>
          )}
        </div>
      )}

      {/* Cases View */}
      {subView === 'cases' && (
        <div className="space-y-2">
          {/* Status filter */}
          <div className="mb-3">
            <select
              value={caseStatusFilter}
              onChange={(e) => setCaseStatusFilter(e.target.value as CalibrationCaseStatus | 'all')}
              className="border border-zinc-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-rose-500"
            >
              <option value="all">All Statuses</option>
              <option value="imported">Imported</option>
              <option value="extracted">Extracted</option>
              <option value="compared">Compared</option>
              <option value="reviewed">Reviewed</option>
              <option value="complete">Complete</option>
              <option value="failed">Failed</option>
            </select>
            <span className="text-[13px] text-zinc-500 ml-3">
              {filteredCases.length} case{filteredCases.length !== 1 ? 's' : ''}
            </span>
          </div>

          {loading ? (
            <p className="text-sm text-zinc-500 py-4">Loading cases...</p>
          ) : filteredCases.length === 0 ? (
            <p className="text-sm text-zinc-500 py-8 text-center">
              {cases.length === 0
                ? 'No calibration cases yet. Import paired SOAP + Lab files to begin.'
                : 'No cases match the selected filter.'}
            </p>
          ) : (
            filteredCases.map((c) => (
              <CalibrationCaseCard key={c.caseId} calibrationCase={c} />
            ))
          )}
        </div>
      )}

      {/* Dr. C Review Queue */}
      {subView === 'review' && (
        <DrCReviewQueue brandId={BRAND_ID} />
      )}

      {/* Convergence Trend */}
      {subView === 'convergence' && (
        <ConvergenceTrend stats={convergenceStats} loading={convergenceLoading} />
      )}

      {/* Launch Gate */}
      {subView === 'launch-gate' && gateStatus && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-['Instrument_Serif'] text-lg text-zinc-900">
              PI-007 Launch Gate
            </h3>
            <span className={`text-sm font-semibold px-3 py-1 rounded ${
              gateStatus.passing ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
            }`}>
              {gateStatus.passing ? 'PASSING' : 'NOT YET'}
            </span>
          </div>

          <GateProgressBar label="Cases Reviewed" current={gateStatus.totalCasesReviewed} target={gateStatus.thresholds.minCases} />
          <GateProgressBar label="Women 35-60" current={gateStatus.womenCases35to60} target={gateStatus.thresholds.minWomenCases} />
          <GateProgressBar label="Overall Alignment" current={gateStatus.overallAlignmentScore} target={gateStatus.thresholds.minOverallAlignment} unit="%" />
          <GateProgressBar label="DP5 (Product Selection)" current={gateStatus.dp5Score} target={gateStatus.thresholds.minDP5} unit="%" />
          <GateProgressBar label="DP7 (Missed Signals)" current={gateStatus.dp7Score} target={gateStatus.thresholds.minDP7} unit="%" />
          <GateProgressBar label="Unresolved High-Stake" current={gateStatus.unresolvedHighStake} target={gateStatus.thresholds.maxUnresolved} invert />

          {/* Recompute Gate Button */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleRecomputeGate}
              disabled={recomputeLoading}
              className="px-4 py-2 bg-rose-600 text-white rounded text-sm font-semibold hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <RefreshCw size={14} className={recomputeLoading ? 'animate-spin' : ''} />
              {recomputeLoading ? 'Recomputing...' : 'Recompute Gate'}
            </button>
            {recomputedGate && (
              <span className={`text-sm font-medium ${recomputedGate.passing ? 'text-green-600' : 'text-amber-600'}`}>
                Result: {recomputedGate.passing ? 'PASSING' : 'NOT YET'} (saved to Firestore)
              </span>
            )}
          </div>

          {/* Demographic distribution */}
          {cases.length >= 25 && (
            <div className="mt-4 p-4 bg-zinc-50 rounded border border-zinc-100">
              <h4 className="text-[13px] font-semibold text-zinc-500 uppercase tracking-wider mb-2">Demographic Distribution</h4>
              {computeDemographicDistribution(cases).warnings.map((w, i) => (
                <p key={i} className="text-sm text-amber-600">&#9888; {w}</p>
              ))}
              {computeDemographicDistribution(cases).warnings.length === 0 && (
                <p className="text-sm text-green-600">Distribution looks good.</p>
              )}
            </div>
          )}

          {/* Threshold Editor */}
          <div className="mt-4 p-4 bg-white rounded border border-zinc-200">
            <h4 className="text-[13px] font-semibold text-zinc-500 uppercase tracking-wider mb-3">Threshold Settings</h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-[13px] text-zinc-500 block mb-1">Min Cases</label>
                <input
                  type="number"
                  value={thresholdEdits.minCases}
                  onChange={(e) => setThresholdEdits({ ...thresholdEdits, minCases: parseInt(e.target.value) || 0 })}
                  className="w-full border border-zinc-200 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-rose-500"
                />
              </div>
              <div>
                <label className="text-[13px] text-zinc-500 block mb-1">Min Women Cases</label>
                <input
                  type="number"
                  value={thresholdEdits.minWomenCases}
                  onChange={(e) => setThresholdEdits({ ...thresholdEdits, minWomenCases: parseInt(e.target.value) || 0 })}
                  className="w-full border border-zinc-200 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-rose-500"
                />
              </div>
              <div>
                <label className="text-[13px] text-zinc-500 block mb-1">Min Overall Alignment %</label>
                <input
                  type="number"
                  value={thresholdEdits.minOverallAlignment}
                  onChange={(e) => setThresholdEdits({ ...thresholdEdits, minOverallAlignment: parseInt(e.target.value) || 0 })}
                  className="w-full border border-zinc-200 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-rose-500"
                />
              </div>
              <div>
                <label className="text-[13px] text-zinc-500 block mb-1">Min DP5 %</label>
                <input
                  type="number"
                  value={thresholdEdits.minDP5}
                  onChange={(e) => setThresholdEdits({ ...thresholdEdits, minDP5: parseInt(e.target.value) || 0 })}
                  className="w-full border border-zinc-200 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-rose-500"
                />
              </div>
              <div>
                <label className="text-[13px] text-zinc-500 block mb-1">Min DP7 %</label>
                <input
                  type="number"
                  value={thresholdEdits.minDP7}
                  onChange={(e) => setThresholdEdits({ ...thresholdEdits, minDP7: parseInt(e.target.value) || 0 })}
                  className="w-full border border-zinc-200 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-rose-500"
                />
              </div>
              <div>
                <label className="text-[13px] text-zinc-500 block mb-1">Max Unresolved</label>
                <input
                  type="number"
                  value={thresholdEdits.maxUnresolved}
                  onChange={(e) => setThresholdEdits({ ...thresholdEdits, maxUnresolved: parseInt(e.target.value) || 0 })}
                  className="w-full border border-zinc-200 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-rose-500"
                />
              </div>
            </div>
            <div className="mt-3 flex items-center gap-3">
              <button
                onClick={handleSaveThresholds}
                disabled={thresholdsLoading}
                className="px-4 py-2 bg-zinc-900 text-white rounded text-sm font-semibold hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {thresholdsLoading ? <RefreshCw size={14} className="animate-spin" /> : <Settings size={14} />}
                {thresholdsLoading ? 'Saving...' : 'Save Thresholds'}
              </button>
              {thresholdsSaved && (
                <span className="text-sm text-green-600 font-medium">Saved</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Formulary Mappings */}
      {subView === 'mappings' && (
        <FormularyMappingEditor />
      )}

      {/* Improvements (Divergence Summary Approval) */}
      {subView === 'improvements' && (
        <div className="space-y-4">
          <h3 className="font-['Instrument_Serif'] text-lg text-zinc-900">
            Divergence Summary Approval
          </h3>
          <p className="text-[13px] text-zinc-500">
            Every 25 reviewed cases, the system identifies divergence patterns between the model and Dr. C.
            Review proposed prompt changes below, edit the system prompt, then approve or reject.
          </p>

          {summaries.length === 0 ? (
            <p className="text-sm text-zinc-500 py-8 text-center">No divergence summaries generated yet.</p>
          ) : (
            summaries.map((s) => (
              <div
                key={s.summaryId}
                className={`bg-white border rounded p-4 ${
                  s.approvedByStan ? 'border-green-200' : s.rejectionReason ? 'border-zinc-200 opacity-70' : 'border-zinc-200'
                }`}
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-zinc-800">
                      Cases {s.batchRange.start}--{s.batchRange.end}
                    </span>
                    <span className="text-[13px] text-zinc-500">
                      {new Date(s.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  {s.approvedByStan && (
                    <span className="flex items-center gap-1 text-sm text-green-600 font-medium">
                      <CheckCircle size={14} /> Approved
                    </span>
                  )}
                  {s.rejectionReason && !s.approvedByStan && (
                    <span className="flex items-center gap-1 text-sm text-zinc-500 font-medium">
                      <XCircle size={14} /> Rejected
                    </span>
                  )}
                </div>

                {/* Patterns */}
                <div className="mb-3">
                  <h4 className="text-[13px] font-semibold text-zinc-500 uppercase tracking-wider mb-1">Divergence Patterns</h4>
                  <div className="space-y-1">
                    {s.patterns.map((p, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <span className="font-medium text-zinc-800 shrink-0">{p.decisionArea}</span>
                        <span className="text-zinc-500">({p.frequency}x)</span>
                        <span className="text-zinc-600 truncate">{p.description}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Proposed change */}
                <div className="mb-3">
                  <h4 className="text-[13px] font-semibold text-zinc-500 uppercase tracking-wider mb-1">Proposed Prompt Change</h4>
                  <p className="text-sm text-zinc-700 bg-zinc-50 rounded p-3 whitespace-pre-wrap">{s.proposedPromptChange}</p>
                </div>

                {/* Actions (only if not already approved) */}
                {!s.approvedByStan && !s.rejectionReason && (
                  <div className="space-y-3 border-t border-zinc-100 pt-3">
                    {/* Updated system prompt textarea */}
                    <div>
                      <label className="text-[13px] text-zinc-500 block mb-1">
                        Full Updated System Prompt (paste/edit the final version)
                      </label>
                      <textarea
                        value={summaryPrompts[s.summaryId] ?? ''}
                        onChange={(e) => setSummaryPrompts({ ...summaryPrompts, [s.summaryId]: e.target.value })}
                        placeholder="Paste the updated system prompt here..."
                        rows={6}
                        className="w-full border border-zinc-200 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-rose-500 resize-y"
                      />
                    </div>

                    <div className="flex items-center gap-3">
                      {/* Approve button */}
                      <button
                        onClick={() => handleApproveSummary(s.summaryId)}
                        disabled={!summaryPrompts[s.summaryId]?.trim() || summaryActionLoading === s.summaryId}
                        className="px-4 py-2 bg-green-600 text-white rounded text-sm font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {summaryActionLoading === s.summaryId ? (
                          <RefreshCw size={14} className="animate-spin" />
                        ) : (
                          <CheckCircle size={14} />
                        )}
                        Approve & Deploy
                      </button>

                      {/* Reject section */}
                      <div className="flex items-center gap-2 flex-1">
                        <input
                          value={summaryRejectReasons[s.summaryId] ?? ''}
                          onChange={(e) => setSummaryRejectReasons({ ...summaryRejectReasons, [s.summaryId]: e.target.value })}
                          placeholder="Rejection reason..."
                          className="flex-1 border border-zinc-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-rose-500"
                        />
                        <button
                          onClick={() => handleRejectSummary(s.summaryId)}
                          disabled={!summaryRejectReasons[s.summaryId]?.trim() || summaryActionLoading === s.summaryId}
                          className="px-4 py-2 bg-zinc-200 text-zinc-700 rounded text-sm font-semibold hover:bg-zinc-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          <XCircle size={14} />
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Show rejection reason if rejected */}
                {s.rejectionReason && (
                  <p className="text-[13px] text-zinc-500 mt-2">Reason: {s.rejectionReason}</p>
                )}

                {/* Show applied date if approved */}
                {s.approvedByStan && s.appliedAt && (
                  <p className="text-[13px] text-zinc-500 mt-2">
                    Applied: {new Date(s.appliedAt).toLocaleDateString()}
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Helper Components ──

function StatBox({ label, value, color }: { label: string; value: string | number; color?: string }) {
  return (
    <div className="bg-white border border-zinc-200 rounded px-4 py-3 flex-1">
      <p className="text-[13px] text-zinc-500">{label}</p>
      <p className={`text-lg font-semibold ${color || 'text-zinc-900'}`}>{value}</p>
    </div>
  );
}

function GateProgressBar({
  label, current, target, unit = '', invert = false,
}: {
  label: string;
  current: number;
  target: number;
  unit?: string;
  invert?: boolean;
}) {
  const passing = invert ? current <= target : current >= target;
  const pct = invert
    ? (target === 0 && current === 0 ? 100 : current <= target ? 100 : 0)
    : Math.min(100, target > 0 ? (current / target) * 100 : 0);

  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-zinc-700">{label}</span>
        <span className={passing ? 'text-green-600 font-medium' : 'text-zinc-500'}>
          {current}{unit} / {invert ? '≤' : '≥'}{target}{unit}
          {passing ? ' ✓' : ''}
        </span>
      </div>
      <div className="h-2 bg-zinc-100 rounded overflow-hidden">
        <div
          className={`h-full rounded transition-all ${passing ? 'bg-green-500' : 'bg-amber-400'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ── Convergence Trend Component ──

function ConvergenceTrend({
  stats,
  loading,
}: {
  stats: {
    total: number;
    modelCorrect: number;
    drCorrect: number;
    bothCorrect: number;
    avgAlignment: number;
    modelCorrectRate: number;
    trendByBatch: Array<{ batchStart: number; batchEnd: number; avgAlignment: number }>;
    biasWarning: string | null;
  } | null;
  loading: boolean;
}) {
  if (loading) {
    return <p className="text-sm text-zinc-500 py-4">Loading convergence data...</p>;
  }

  if (!stats || stats.total === 0) {
    return (
      <div className="text-center py-8 text-zinc-500">
        <TrendingUp size={32} className="mx-auto mb-2" />
        <p className="text-sm">No convergence data yet.</p>
        <p className="text-[13px] mt-1">Data appears after Dr. C submits reviews.</p>
      </div>
    );
  }

  const maxAlignment = 100;
  const trend = stats.trendByBatch;
  const isConverging = trend.length >= 2 && trend[trend.length - 1].avgAlignment > trend[0].avgAlignment;
  const isDiverging = trend.length >= 2 && trend[trend.length - 1].avgAlignment < trend[0].avgAlignment;

  return (
    <div className="space-y-4">
      <h3 className="font-['Instrument_Serif'] text-lg text-zinc-900">
        Captain &harr; Dr. C Convergence
      </h3>

      {/* Summary metrics */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-white border border-zinc-200 rounded px-4 py-3">
          <p className="text-[13px] text-zinc-500">Reviews</p>
          <p className="text-lg font-semibold text-zinc-900">{stats.total}</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded px-4 py-3">
          <p className="text-[13px] text-zinc-500">Model Correct</p>
          <p className="text-lg font-semibold text-zinc-900">{Math.round(stats.modelCorrectRate * 100)}%</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded px-4 py-3">
          <p className="text-[13px] text-zinc-500">Avg Alignment</p>
          <p className="text-lg font-semibold text-zinc-900">{stats.avgAlignment}%</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded px-4 py-3">
          <p className="text-[13px] text-zinc-500">Direction</p>
          <p className={`text-lg font-semibold ${
            isConverging ? 'text-green-600' : isDiverging ? 'text-red-600' : 'text-zinc-500'
          }`}>
            {isConverging ? 'Converging' : isDiverging ? 'Diverging' : 'Stable'}
          </p>
        </div>
      </div>

      {/* Bias warning */}
      {stats.biasWarning && (
        <div className="bg-amber-50 border border-amber-200 rounded p-3">
          <p className="text-sm text-amber-800 font-medium">Bias Warning</p>
          <p className="text-[13px] text-amber-700 mt-1">{stats.biasWarning}</p>
        </div>
      )}

      {/* Trend chart — pure CSS bar chart */}
      {trend.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded p-4">
          <h4 className="text-[13px] font-semibold text-zinc-500 uppercase tracking-wider mb-3">
            Alignment Trend (batches of 25)
          </h4>
          <div className="flex items-end gap-2" style={{ height: 160 }}>
            {trend.map((batch, i) => {
              const height = Math.max(4, (batch.avgAlignment / maxAlignment) * 140);
              const isLatest = i === trend.length - 1;
              const barColor = batch.avgAlignment >= 75
                ? 'bg-green-500'
                : batch.avgAlignment >= 50
                  ? 'bg-amber-400'
                  : 'bg-red-400';

              return (
                <div key={i} className="flex-1 flex flex-col items-center justify-end" style={{ height: '100%' }}>
                  <span className="text-[11px] font-mono text-zinc-600 mb-1">{batch.avgAlignment}%</span>
                  <div
                    className={`w-full rounded-t ${barColor} ${isLatest ? 'ring-2 ring-rose-500 ring-offset-1' : ''}`}
                    style={{ height, transition: 'height 0.3s ease' }}
                  />
                  <span className="text-[11px] text-zinc-400 mt-1">
                    {batch.batchStart}–{batch.batchEnd}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 mt-3 text-[11px] text-zinc-500">
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded bg-green-500 inline-block" /> &ge;75%
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded bg-amber-400 inline-block" /> 50–74%
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded bg-red-400 inline-block" /> &lt;50%
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded ring-2 ring-rose-500 inline-block" /> Latest batch
            </span>
          </div>
        </div>
      )}

      {/* Detailed breakdown */}
      <div className="bg-white border border-zinc-200 rounded p-4">
        <h4 className="text-[13px] font-semibold text-zinc-500 uppercase tracking-wider mb-3">
          Agreement Breakdown
        </h4>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-zinc-600">Model rated correct (Yes/Mostly)</span>
            <span className="font-mono text-zinc-800">{stats.modelCorrect} / {stats.total}</span>
          </div>
          <div className="h-2 bg-zinc-100 rounded overflow-hidden">
            <div
              className="h-full bg-green-500 rounded"
              style={{ width: `${stats.total > 0 ? (stats.modelCorrect / stats.total) * 100 : 0}%`, transition: 'width 0.3s ease' }}
            />
          </div>

          <div className="flex justify-between text-sm mt-3">
            <span className="text-zinc-600">Model rated incorrect (No)</span>
            <span className="font-mono text-zinc-800">{stats.total - stats.modelCorrect} / {stats.total}</span>
          </div>
          <div className="h-2 bg-zinc-100 rounded overflow-hidden">
            <div
              className="h-full bg-red-400 rounded"
              style={{ width: `${stats.total > 0 ? ((stats.total - stats.modelCorrect) / stats.total) * 100 : 0}%`, transition: 'width 0.3s ease' }}
            />
          </div>
        </div>
      </div>

      {/* Feedback loop explanation */}
      <div className="bg-zinc-50 border border-zinc-100 rounded p-3">
        <p className="text-[13px] text-zinc-500">
          <strong>Feedback loop:</strong> Every 25 reviews, divergence patterns are analyzed and a prompt improvement is proposed.
          Approve changes in the Improvements tab. {stats.total >= 25 ? `Next summary at review #${Math.ceil(stats.total / 25) * 25 + (stats.total % 25 === 0 ? 0 : 25)}.` : `First summary generates at 25 reviews (${25 - stats.total} remaining).`}
        </p>
      </div>
    </div>
  );
}

// ── Launch Gate Computation ──

function computeLaunchGate(
  cases: CalibrationCase[],
  stats: { total: number; avgAlignment: number; reviewedCount: number },
): LaunchGateStatus {
  const thresholds = {
    minCases: 50,
    minWomenCases: 20,
    minOverallAlignment: 75,
    minDP5: 85,
    minDP7: 85,
    maxUnresolved: 0,
  };

  const reviewed = cases.filter((c) => c.drCReview);
  const demo = computeDemographicDistribution(cases);

  // Compute per-DP scores
  let dp5Total = 0, dp5Aligned = 0;
  let dp7Total = 0, dp7Aligned = 0;
  let unresolvedHighStake = 0;

  for (const c of reviewed) {
    if (!c.decisionPoints) continue;
    for (const dp of c.decisionPoints) {
      if (dp.area === 'Product Selection') {
        dp5Total++;
        if (dp.alignment === 'aligned') dp5Aligned++;
      }
      if (dp.area === 'Missed Signals') {
        dp7Total++;
        if (dp.alignment === 'aligned') dp7Aligned++;
      }
      // High-stake divergence: Primary Dx or Supplement-First with delta
      if (['Primary Dx', 'Supplement-First'].includes(dp.area) && dp.alignment === 'delta') {
        if (c.drCReview?.rating === 'no') {
          unresolvedHighStake++;
        }
      }
    }
  }

  const dp5Score = dp5Total > 0 ? Math.round((dp5Aligned / dp5Total) * 100) : 0;
  const dp7Score = dp7Total > 0 ? Math.round((dp7Aligned / dp7Total) * 100) : 0;

  const passing =
    stats.reviewedCount >= thresholds.minCases &&
    demo.womenCases35to60 >= thresholds.minWomenCases &&
    stats.avgAlignment >= thresholds.minOverallAlignment &&
    dp5Score >= thresholds.minDP5 &&
    dp7Score >= thresholds.minDP7 &&
    unresolvedHighStake <= thresholds.maxUnresolved;

  return {
    totalCasesReviewed: stats.reviewedCount,
    womenCases35to60: demo.womenCases35to60,
    overallAlignmentScore: stats.avgAlignment,
    dp5Score,
    dp7Score,
    unresolvedHighStake,
    thresholds,
    passing,
    computedAt: new Date().toISOString(),
  };
}
